import { Button, Flex, Text } from '@chakra-ui/react';
import { GenericSubmissionField } from './MySubmission/FormFieldGeneric/GenericSubmissionField';
import { PhaseBanner } from './PhaseBanner';
import { CommandResponseProvider } from 'src/contexts/SourceFileCommandResponseContex';
import GroupRenderer from './MySubmission/FormFieldGeneric/GroupRenderer';
import { OutlineCard as AlembicOutlineCard } from '@morressier/alembic';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import NotifyAuthorConfirmationModal from './MySubmission/components/NotifyAuthorConfirmationModal';
import ProtectedWorkflowAction from '../ProtectedAction/ProtectedWorkflowAction';
import PreferencesTab from './PreferencesTab/PreferencesTab';

export const OutlineCard: React.FC<{ children: React.ReactNode }> = ({
  children
}) => (
  <AlembicOutlineCard
    borderless
    borderRadius="none"
    flexDirection="column"
    justifyContent="space-between"
    gap={4}
  >
    {children}
  </AlembicOutlineCard>
);

const SubmissionTab: React.FC<{}> = () => {
  const [showNotifyAuthor, setShowNotifyAuthor] = useState(false);

  return (
    <Flex flexDirection="column" gap={5}>
      <PhaseBanner role="editorial" />

      <Flex justifyContent="space-between" alignItems="center">
        <Flex flexDirection="column" alignItems="flex-start" gap={2}>
          <Text textStyle="headingSection">Submission</Text>
          <Text textStyle="textBody">
            Review and manage all information associated with this submission.
          </Text>
        </Flex>

        <ProtectedWorkflowAction actionName="EditSubmission">
          <Button
            variant="primary"
            colorScheme="blue"
            leftIcon={<FontAwesomeIcon icon={faEnvelope} />}
            onClick={() => setShowNotifyAuthor(true)}
          >
            Notify Author
          </Button>
        </ProtectedWorkflowAction>
      </Flex>
      
      <GenericSubmissionField fieldName="FocusIssue" />
      <GenericSubmissionField fieldName="Title" />
      <GenericSubmissionField fieldName="EnglishTitle" />
      <GenericSubmissionField fieldName="Subtitle" />
      <GenericSubmissionField fieldName="EnglishSubtitle" />
      <GenericSubmissionField fieldName="ArticleType" />
      <GenericSubmissionField fieldName="Abstract" />
      <GenericSubmissionField fieldName="EnglishAbstract" />
      <GenericSubmissionField fieldName="Keywords" />
      <GenericSubmissionField fieldName="SubmissionGuidelines" />
      <GenericSubmissionField fieldName="EnglishKeywords" />
      <GenericSubmissionField fieldName="ArticleSubtype" />
      <GenericSubmissionField fieldName="ArticleLanguage" />
      <GenericSubmissionField fieldName="Category" />
      <GenericSubmissionField fieldName="CategoryOther" />
      <GenericSubmissionField fieldName="FiguresAndTables" />

      <Flex flexDirection="column" gap={4}>
        <Flex justifyContent="space-between">
          <Text textStyle="headingSection">Submission Files</Text>
        </Flex>
        <Flex flexDirection={'column'} gap={5}>
          <CommandResponseProvider>
            <GenericSubmissionField fieldName="SourceFiles" />
          </CommandResponseProvider>
          <GenericSubmissionField fieldName="Manuscript" />
          <GenericSubmissionField fieldName="TitlePage" />
          <GenericSubmissionField fieldName="ConsolidatedPdf" />
          <GenericSubmissionField fieldName="AnonymizedConsolidatedPdf" />
          <GenericSubmissionField fieldName="CompleteDocumentForReview" />
          <GenericSubmissionField fieldName="AnonymizedManuscript" />
          <GenericSubmissionField
            fieldName="Supplementary"
            showSubtitle={false}
          />
          <GenericSubmissionField
            fieldName="AdditionalSupplementary"
            showSubtitle={false}
          />
          <GroupRenderer
            showSubtitle={false}
            fields={[
              {
                name: 'CoverLetter',
                renderMethod: element => <OutlineCard>{element}</OutlineCard>
              },
              {
                name: 'StandardReportingGuidelines',
                renderMethod: el => <OutlineCard>{el}</OutlineCard>
              }
            ]}
          >
            {renderedFields => (
              <Flex gap={8} flexDirection="column">
                {renderedFields}
              </Flex>
            )}
          </GroupRenderer>
          <GroupRenderer
            fields={[
              {
                name: 'Figures',
                renderMethod: el => <OutlineCard>{el}</OutlineCard>
              },
              {
                name: 'GraphicalAbstractImage',
                renderMethod: el => <OutlineCard>{el}</OutlineCard>
              },
              {
                name: 'Tables',
                renderMethod: el => <OutlineCard>{el}</OutlineCard>
              }
            ]}
          >
            {renderedFields => (
              <Flex gap={8} flexDirection="column">
                {renderedFields}
              </Flex>
            )}
          </GroupRenderer>
          <GenericSubmissionField fieldName="NumberOfFigures" />
          <GenericSubmissionField fieldName="NumberOfColorFigures" />
          <GenericSubmissionField fieldName="NumberOfTables" />
          <GenericSubmissionField fieldName="NumberOfWords" />
          <GenericSubmissionField fieldName="GeneralClassifications" />
        </Flex>
        <PreferencesTab />
      </Flex>

      <NotifyAuthorConfirmationModal
        isOpen={showNotifyAuthor}
        onClose={() => setShowNotifyAuthor(false)}
      />
    </Flex>
  );
};

export default SubmissionTab;
